import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import { ChevronDownIcon, CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";

// Modal styles matching EngineerNotesModal
const offerModalStyle = {
    content: {
        width: "60%",
        maxHeight: "80%",
        margin: "auto",
        overflow: "auto",
        padding: "20px",
    },
};

const OfferModal = ({ isOpen, onClose, offerData, selectedOffers, onSave }) => {
    const [localSelectedOffers, setLocalSelectedOffers] = useState({
        offerValidity: null,
        delivery: null,
        payment: null,
        incoterm: null,
        packing: null,
        warranty: null,
        forceMajeure: null
    });

    const [summaryString, setSummaryString] = useState("");
    const [openDropdown, setOpenDropdown] = useState(null);
    const dropdownRefs = useRef({});

    // Initialize with existing selected offers when modal opens
    useEffect(() => {
        if (isOpen && selectedOffers) {
            setLocalSelectedOffers(selectedOffers);
        }
    }, [selectedOffers, isOpen]);

    // Update summary string whenever selections change
    useEffect(() => {
        const summary = buildSummaryString(localSelectedOffers);
        setSummaryString(summary);
    }, [localSelectedOffers]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openDropdown && dropdownRefs.current[openDropdown] && 
                !dropdownRefs.current[openDropdown].contains(event.target)) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openDropdown]);

    const buildSummaryString = (offers) => {
        const parts = [];
        
        if (offers.offerValidity) {
            parts.push(`Offer Validity: ${offers.offerValidity.offerValue}`);
        }
        if (offers.delivery) {
            parts.push(`Delivery: ${offers.delivery.offerValue}`);
        }
        if (offers.payment) {
            parts.push(`Payment: ${offers.payment.offerValue}`);
        }
        if (offers.incoterm) {
            parts.push(`Incoterm: ${offers.incoterm.offerValue}`);
        }
        if (offers.packing) {
            parts.push(`Packing: ${offers.packing.offerValue}`);
        }
        if (offers.warranty) {
            parts.push(`Warranty: ${offers.warranty.offerValue}`);
        }
        if (offers.forceMajeure) {
            parts.push(`Force Majeure: ${offers.forceMajeure.offerValue}`);
        }

        return parts.join(" | ");
    };

    const handleOfferChange = (category, offer) => {
        setLocalSelectedOffers(prev => ({
            ...prev,
            [category]: offer
        }));
        setOpenDropdown(null);
    };

    const handleClearSelection = (category) => {
        setLocalSelectedOffers(prev => ({
            ...prev,
            [category]: null
        }));
        setOpenDropdown(null);
    };

    const handleSave = () => {
        const offers = {
            offerValidity: localSelectedOffers.offerValidity,
            delivery: localSelectedOffers.delivery,
            payment: localSelectedOffers.payment,
            incoterm: localSelectedOffers.incoterm,
            packing: localSelectedOffers.packing,
            warranty: localSelectedOffers.warranty,
            forceMajeure: localSelectedOffers.forceMajeure
        };
        
        onSave(offers);
        onClose();
    };

    const handleClose = () => {
        if (selectedOffers) {
            setLocalSelectedOffers(selectedOffers);
        }
        onClose();
    };

    const getOfferData = (key) => {
        if (!offerData) return [];
        
        const keyMapping = {
            'offerValidity': 'Offer Validity',
            'delivery': 'Delivery',
            'payment': 'Payment',
            'incoterm': 'Incoterm',
            'packing': 'Packing',
            'warranty': 'Warranty',
            'forceMajeure': 'Force Majeure'
        };
        
        const mappedKey = keyMapping[key] || key;
        return offerData[mappedKey] || [];
    };

    const getCategoryLabel = (category) => {
        const labels = {
            'offerValidity': 'Offer Validity',
            'delivery': 'Delivery',
            'payment': 'Payment',
            'incoterm': 'Incoterm',
            'packing': 'Packing',
            'warranty': 'Warranty',
            'forceMajeure': 'Force Majeure'
        };
        return labels[category] || category;
    };

    // Truncate long text for display
    const truncateText = (text, maxLength = 50) => {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    const renderDropdown = (category) => {
        const options = getOfferData(category);
        const label = getCategoryLabel(category);
        const selectedValue = localSelectedOffers[category]?.offerId || "";
        const selectedOption = localSelectedOffers[category];
        const isOpen = openDropdown === category;

        return (
            <div className="relative" ref={el => dropdownRefs.current[category] = el}>
                <label className="block text-sm font-medium text-gray-700">
                    {label}
                </label>
                <div
                    className={`w-full px-3 py-2 mt-1 bg-white border rounded-md cursor-pointer flex items-center justify-between ${
                        selectedValue 
                            ? 'border-primary-500' 
                            : 'border-gray-300'
                    } ${isOpen ? 'border-primary-500 ring-2 ring-primary-200' : ''}`}
                    onClick={() => setOpenDropdown(isOpen ? null : category)}
                >
                    <span className={`text-sm flex-1 truncate pr-2 ${selectedValue ? 'text-gray-900' : 'text-gray-500'}`}>
                        {selectedValue ? truncateText(selectedOption?.offerValue, 45) : `Select ${label}`}
                    </span>
                    <ChevronDownIcon className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>

                {isOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {selectedValue && (
                            <div
                                className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 flex items-center justify-between border-b border-gray-100 text-red-600"
                                onClick={() => handleClearSelection(category)}
                            >
                                <span>Clear selection</span>
                                <XMarkIcon className="w-4 h-4 flex-shrink-0" />
                            </div>
                        )}
                        
                        {options.map((option) => (
                            <div
                                key={option.offerId}
                                className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 flex items-center justify-between ${
                                    selectedValue === option.offerId ? 'bg-primary-50' : ''
                                }`}
                                onClick={() => handleOfferChange(category, option)}
                            >
                                <span className={`flex-1 pr-2 ${selectedValue === option.offerId ? 'text-primary-700 font-medium' : 'text-gray-700'}`}>
                                    {option.offerValue}
                                </span>
                                {selectedValue === option.offerId && (
                                    <CheckIcon className="w-4 h-4 text-primary-600 flex-shrink-0" />
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {(!options || options.length === 0) && (
                    <div className="w-full px-3 py-2 mt-1 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-400">
                        No options available
                    </div>
                )}
            </div>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            shouldCloseOnOverlayClick={true}
            style={offerModalStyle}
            contentLabel="Offer Options"
        >
            <div className="grid gap-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900">
                        Offer Options
                    </h3>
                    <button
                        onClick={handleClose}
                        className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                    >
                        <XMarkIcon className="h-5 w-5 text-gray-400" />
                    </button>
                </div>

                <p className="text-sm text-gray-500 -mt-2">
                    Configure offer options for your quotation
                </p>

                {/* Dropdown Grid - Fixed with min-width to prevent layout shift */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="min-w-0">
                        {renderDropdown('offerValidity')}
                    </div>
                    <div className="min-w-0">
                        {renderDropdown('delivery')}
                    </div>
                    <div className="min-w-0">
                        {renderDropdown('payment')}
                    </div>
                    <div className="min-w-0">
                        {renderDropdown('incoterm')}
                    </div>
                    <div className="min-w-0">
                        {renderDropdown('packing')}
                    </div>
                    <div className="min-w-0">
                        {renderDropdown('warranty')}
                    </div>
                    <div className="min-w-0 md:col-span-2">
                        {renderDropdown('forceMajeure')}
                    </div>
                </div>

                {/* Summary Section */}
                {summaryString && (
                    <div className="mt-2 p-3 bg-blue-50 rounded-md border border-blue-200">
                        <p className="text-sm text-blue-700 break-words">
                            <span className="font-medium">Summary:</span> {summaryString}
                        </p>
                    </div>
                )}

                {/* Footer */}
                <div className="flex flex-row gap-2 pt-2 border-t border-gray-200 mt-2">
                    <button
                        onClick={handleSave}
                        className="inline-flex justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    >
                        Save Offer
                    </button>
                    <button
                        onClick={handleClose}
                        className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default OfferModal;